import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { teamUserService, type Role, type Permission } from '@/services/teamUserService';
import { toast } from 'react-hot-toast';

interface ViewRoleDrawerProps {
  open: boolean;
  onClose: () => void;
  roleUuid: string | null;
}

export const ViewRoleDrawer: React.FC<ViewRoleDrawerProps> = ({
  open,
  onClose,
  roleUuid,
}) => {
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    if (open && roleUuid) {
      loadRoleDetails();
      loadAllPermissions();
    } else {
      setRole(null);
    }
  }, [open, roleUuid]);

  const loadRoleDetails = async () => {
    if (!roleUuid) return;
    
    setIsLoading(true);
    try {
      const roles = await teamUserService.getRoles();
      const foundRole = roles.find((r) => r.uuid === roleUuid);
      if (foundRole) {
        setRole(foundRole);
      } else {
        toast.error('Role not found', {
          style: { background: '#d32f2f', color: '#fff' },
        });
        onClose();
      }
    } catch (error) {
      console.error('Error loading role details:', error);
      toast.error('Failed to load role details', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllPermissions = async () => {
    try {
      const data = await teamUserService.getPermissionList();
      setAllPermissions(data);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Group permissions by section
  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    const section = perm.section;
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Check if a permission is in the role
  const isPermissionInRole = (permissionUuid: string): boolean => {
    return role?.permissions?.some((p) => p.uuid === permissionUuid) || false;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600, md: 800 },
          maxWidth: '90vw',
        },
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Role Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : role ? (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Role Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Role Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Role Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {role.role_name}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Members
                </Typography>
                <Chip
                  label={role.user_count || 0}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 'normal',
                    fontSize: '0.75rem',
                    borderRadius: '12px',
                  }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Created at
                </Typography>
                <Typography variant="body1">
                  {formatDate(role.created_at)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Permissions */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Permissions ({role.permissions?.length || 0})
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Permission</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(groupedPermissions).map(([section, sectionPermissions]) =>
                      sectionPermissions.map((perm) => {
                        const isInRole = isPermissionInRole(perm.uuid);
                        return (
                          <TableRow key={perm.uuid}>
                            <TableCell>{section}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {perm.permission_name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {perm.description}
                                </Typography>
                                {perm.permission_name.includes('ACCOUNTS') ||
                                perm.permission_name.includes('TEAM') ||
                                perm.permission_name.includes('KEYS') ? (
                                  <Tooltip title="Warning: This permission grants sensitive access">
                                    <WarningIcon
                                      sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isInRole ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={isInRole ? 'success' : 'default'}
                                variant={isInRole ? 'filled' : 'outlined'}
                                sx={{
                                  fontSize: '0.7rem',
                                  height: '24px',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="body1" color="textSecondary">
              No role data available
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

